import React, { useState } from 'react';
import { GlassCard, GlassBadge, GlassButton } from '@/design-system';
import {
  Target,
  ChevronDown,
  ChevronRight,
  Edit2,
  Trash2,
  Eye,
  TrendingUp,
  Building2,
  Layers,
  FileCode,
  MoreHorizontal,
} from 'lucide-react';
import type { Objective, KeyResult, OKRLevel, OKRStatusNew } from '@/types';

interface OKRObjectiveCardProps {
  objective: Objective;
  onView: (objective: Objective) => void;
  onEdit: (objective: Objective) => void;
  onDelete: (id: string) => void;
  onStatusChange: (id: string, status: string) => void;
  onProgressUpdate: (keyResult: KeyResult) => void;
  getLevelColor: (level?: OKRLevel) => string;
  getStatusColor: (status: OKRStatusNew) => string;
}

const OKRObjectiveCard: React.FC<OKRObjectiveCardProps> = ({
  objective,
  onView,
  onEdit,
  onDelete,
  onStatusChange,
  onProgressUpdate,
  getLevelColor,
  getStatusColor,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showActions, setShowActions] = useState(false);

  const getLevelIcon = (level?: OKRLevel) => {
    switch (level) {
      case 'IS':
        return <Layers className="w-4 h-4" />;
      case 'PROGRAM':
        return <Building2 className="w-4 h-4" />;
      case 'EPIC':
        return <FileCode className="w-4 h-4" />;
      default:
        return <Target className="w-4 h-4" />;
    }
  };

  const getProgressColor = (progress: number | null | undefined) => {
    if (progress === null || progress === undefined) return 'bg-gray-500';
    if (progress >= 75) return 'bg-green-500';
    if (progress >= 50) return 'bg-yellow-500';
    if (progress >= 25) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const keyResults = objective.keyResults || [];
  const hasKeyResults = keyResults.length > 0;
  const progress = objective.overallProgress ?? 0;

  return (
    <GlassCard className="overflow-hidden">
      {/* Main Card Content */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-4">
          {/* Left: Expand/Collapse + Title */}
          <div className="flex items-start gap-3 flex-1">
            {/* Expand button */}
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className={`p-1 rounded hover:bg-[var(--bg-hover)]/50 transition-colors ${
                !hasKeyResults ? 'invisible' : ''
              }`}
            >
              {isExpanded ? (
                <ChevronDown className="w-5 h-5 text-muted-foreground" />
              ) : (
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              )}
            </button>

            {/* Level Icon */}
            <div
              className={`p-2 rounded-lg bg-${getLevelColor(objective.level)}-500/20 text-${getLevelColor(objective.level)}-400`}
            >
              {getLevelIcon(objective.level)}
            </div>

            {/* Title and metadata */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3
                  onClick={() => onView(objective)}
                  className="text-base font-medium text-foreground hover:text-blue-400 cursor-pointer transition-colors"
                >
                  {objective.title}
                </h3>
                <GlassBadge variant={getStatusColor(objective.status) as any}>
                  {objective.status}
                </GlassBadge>
                <GlassBadge variant="default">
                  {objective.level || 'Unknown'} Level
                </GlassBadge>
              </div>

              <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                <span>
                  Q{objective.quarter} FY{objective.fiscalYear.toString().slice(-2)}
                </span>
                {objective.entityName && (
                  <>
                    <span className="text-gray-600">|</span>
                    <span>{objective.entityName}</span>
                  </>
                )}
                {hasKeyResults && (
                  <>
                    <span className="text-gray-600">|</span>
                    <span>{keyResults.length} Key Result{keyResults.length !== 1 ? 's' : ''}</span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Right: Progress and Actions */}
          <div className="flex items-center gap-4">
            {/* Progress */}
            <div className="text-right">
              <div className="text-2xl font-bold text-foreground">{Math.round(progress)}%</div>
              <div className="w-24 h-2 bg-[var(--bg-tertiary)]/50 rounded-full overflow-hidden mt-1">
                <div
                  className={`h-full ${getProgressColor(progress)} transition-all duration-300`}
                  style={{ width: `${Math.min(progress, 100)}%` }}
                />
              </div>
            </div>

            {/* Actions */}
            <div className="relative">
              <GlassButton
                variant="ghost"
                size="sm"
                onClick={() => setShowActions(!showActions)}
              >
                <MoreHorizontal className="w-4 h-4" />
              </GlassButton>

              {showActions && (
                <div className="absolute right-0 top-full mt-1 w-40 bg-[var(--bg-elevated)] border border-[var(--border-primary)] rounded-lg shadow-xl z-10">
                  <button
                    onClick={() => {
                      onView(objective);
                      setShowActions(false);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:bg-[var(--bg-tertiary)]/50"
                  >
                    <Eye className="w-4 h-4" />
                    View Details
                  </button>
                  <button
                    onClick={() => {
                      onEdit(objective);
                      setShowActions(false);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:bg-[var(--bg-tertiary)]/50"
                  >
                    <Edit2 className="w-4 h-4" />
                    Edit
                  </button>
                  <button
                    onClick={() => {
                      onDelete(objective.id);
                      setShowActions(false);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-red-500/10"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Description */}
        {objective.description && (
          <p className="mt-3 ml-14 text-sm text-muted-foreground line-clamp-2">
            {objective.description}
          </p>
        )}
      </div>

      {/* Expanded Key Results */}
      {isExpanded && hasKeyResults && (
        <div className="border-t border-[var(--border-primary)] bg-[var(--bg-tertiary)]/50">
          <div className="p-4 space-y-3">
            <h4 className="text-sm font-medium text-muted-foreground mb-3">Key Results</h4>
            {keyResults.map((kr) => (
              <div
                key={kr.id}
                className="flex items-center justify-between p-3 bg-[var(--bg-tertiary)]/50 rounded-lg"
              >
                <div className="flex-1 min-w-0 mr-4">
                  <p className="text-sm text-foreground">{kr.description}</p>
                  <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                    <span>
                      {kr.currentValue ?? kr.startValue} / {kr.targetValue} {kr.unit || ''}
                    </span>
                    {kr.confidence !== null && kr.confidence !== undefined && (
                      <span className="flex items-center gap-1">
                        <span
                          className={`w-2 h-2 rounded-full ${
                            kr.confidence >= 70
                              ? 'bg-green-500'
                              : kr.confidence >= 40
                              ? 'bg-yellow-500'
                              : 'bg-red-500'
                          }`}
                        />
                        {kr.confidence}% confidence
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  {/* Progress bar */}
                  <div className="w-20">
                    <div className="text-right text-sm text-foreground mb-1">
                      {Math.round(kr.progress ?? 0)}%
                    </div>
                    <div className="w-full h-1.5 bg-[var(--bg-tertiary)]/50 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${getProgressColor(kr.progress)} transition-all duration-300`}
                        style={{ width: `${Math.min(kr.progress ?? 0, 100)}%` }}
                      />
                    </div>
                  </div>

                  {/* Update button */}
                  <GlassButton
                    variant="ghost"
                    size="sm"
                    onClick={() => onProgressUpdate(kr)}
                  >
                    <TrendingUp className="w-4 h-4" />
                  </GlassButton>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Click outside to close actions */}
      {showActions && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setShowActions(false)}
        />
      )}
    </GlassCard>
  );
};

export default OKRObjectiveCard;
